import React from "react"
import {
  Alert,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useFormikContext } from "formik"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { TemplateFormData, Vehicle, IoTOdometerStatus, OdometerDataSource } from "../CreateTemplateModal.interfaces"
import { useTranslation } from "react-i18next"
import { StyledHeaderTableCell, StyledTableCell, StyledVehicleBox } from "../CreateTemplateModal.styled"
import { StyledRowGapBox } from "shared/styled/General"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo, faInfoCircle } from "@fortawesome/pro-solid-svg-icons"
import { StyledInputLabel } from "components/Form/FormComponents/FormSwitch/FormSwitch.styled"
import VehicleSwitch from "../VehicleSwitch"

interface LastCheckValuesStepProps {
  vehicles: Vehicle[]
  showInternalNumber: boolean
  sessionServiceValues: any
  setSessionServiceValues: React.Dispatch<React.SetStateAction<any>>
  originalServiceValues: any
  vehicleInspectionData: { [vehicleId: number]: any }
  vehicleIoTOdometerData: { [vehicleId: number]: IoTOdometerStatus }
  fetchInspectionDataForVehicles: (vehicleIds: number[], forceRefresh: boolean) => void
  setShowInternalNumber: React.Dispatch<React.SetStateAction<boolean>>
}

const inputPreviousValuesDesc = `Ange datum och/eller mätarställning för <strong style={{ textDecoration: "underline" }}>senaste utförda</strong> service.
Dessa värden används för att beräkna nästa planerade kontroll.`

export const LastCheckValuesStep: React.FC<LastCheckValuesStepProps> = ({
  vehicles,
  showInternalNumber,
  sessionServiceValues,
  setSessionServiceValues,
  originalServiceValues,
  vehicleInspectionData,
  vehicleIoTOdometerData,
  fetchInspectionDataForVehicles,
  setShowInternalNumber
}) => {
  const theme = useTheme()
  const formik = useFormikContext<TemplateFormData>()
  const { t } = useTranslation()

  const selectedVehiclesList = vehicles.filter(v =>
    formik.values.selectedVehicles.includes(v.id)
  )

  // Fetch inspection data if needed and not already loaded
  if (formik.values.usesInspection && selectedVehiclesList.length > 0 && Object.keys(vehicleInspectionData).length === 0) {
    fetchInspectionDataForVehicles(formik.values.selectedVehicles, true)
  }

  return (
    <Box>
      {formik.values.usesInspection && !formik.values.usesDate && !formik.values.usesDistance ? (
        <Grid container spacing={3} >
          <Grid item xs={12}>
            <StyledRowGapBox sx={{ gap: 1 }}>
              <Typography variant="h3">
                {t("checks.previous_values", "Previous service values")}
              </Typography>
              <Tooltip
                title={t("checks.inspection_based_desc", "The template is inspection-based and retrieves the information automatically from the Swedish Transport Agency.")}>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  color={(theme.palette as any).primary[700]}
                  size="lg"
                />
              </Tooltip>
            </StyledRowGapBox>
          </Grid>

          <Grid item xs={12}>
            <TableContainer >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledHeaderTableCell>{showInternalNumber ? t("global.internal_number", "Internal number") : t("global.license_plate", "License plate")}</StyledHeaderTableCell>
                    <StyledHeaderTableCell>{t("vehicleregistry.latest_approved_inspection", "Latest approved inspection")}</StyledHeaderTableCell>
                    <StyledHeaderTableCell>{t("global.next_inspection", "Next inspection")}</StyledHeaderTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedVehiclesList.map((vehicle) => {
                    return (
                      <TableRow key={vehicle.id}>
                        <StyledTableCell>
                          {showInternalNumber
                            ? (vehicle.internnumber || vehicle.regNumber)
                            : vehicle.regNumber}
                        </StyledTableCell>
                        <StyledTableCell>
                          {vehicleInspectionData[vehicle.id]?.lastInspectionDate
                            ? new Date(vehicleInspectionData[vehicle.id].lastInspectionDate).toLocaleDateString("sv-SE")
                            : t("global.retrieved_automatically", "Retrieved automatically")}
                        </StyledTableCell>
                        <StyledTableCell>
                          {vehicleInspectionData[vehicle.id]?.inspectionDeadline
                            ? new Date(vehicleInspectionData[vehicle.id].inspectionDeadline).toLocaleDateString("sv-SE")
                            : t("global.retrieved_automatically", "Retrieved automatically")}
                        </StyledTableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <StyledRowGapBox sx={{ gap: 1 }}>
              <Typography variant="h4">
                {t("checks.previous_values", "Previous service values")}
              </Typography>
              <Tooltip
                title={
                  t("checks.input_previous_service_values", inputPreviousValuesDesc)
                }>
                <FontAwesomeIcon
                  size="lg"
                  icon={faCircleInfo}
                  color={theme.palette["primary"].dark}
                />
              </Tooltip>
            </StyledRowGapBox>
            <VehicleSwitch setShowValue={setShowInternalNumber} showValue={showInternalNumber} />
          </Grid>
          {selectedVehiclesList.map((vehicle) => (
            <Grid item xs={12} key={vehicle.id}>
              <StyledVehicleBox selectedVehiclesList={selectedVehiclesList}>
                <Grid mb={1} item xs={12}>
                  <Typography variant="h4">{showInternalNumber ? vehicle.internnumber : vehicle.regNumber}</Typography>
                </Grid>
                <Grid container spacing={2}>
                  {formik.values.usesDate && (
                    <Grid item xs={12} sm={6}>
                      <StyledInputLabel>
                        <Typography variant="bodyLarge" color="text.primary">{t("checks.last_service_date", "Last service date")}</Typography>
                      </StyledInputLabel>
                      <DatePicker
                        sx={{ width: "100%" }}
                        disableFuture
                        value={formik.values.vehicleLastValues[vehicle.id]?.lastDate ||
                          sessionServiceValues[vehicle.id]?.lastDate ||
                          originalServiceValues[vehicle.id]?.lastDate || null}
                        onChange={(date) => {
                          formik.setFieldValue(`vehicleLastValues.${vehicle.id}.lastDate`, date, true)
                        }}
                        onAccept={(date) => {
                          formik.setFieldTouched(`vehicleLastValues.${vehicle.id}.lastDate`, true, false)
                          setSessionServiceValues((prev: any) => ({
                            ...prev,
                            [vehicle.id]: { ...prev[vehicle.id], lastDate: date }
                          }))
                        }}
                        slotProps={{
                          textField: {
                            error: Boolean(
                              formik.touched.vehicleLastValues?.[vehicle.id]?.lastDate &&
                              (formik.errors.vehicleLastValues as any)?.[vehicle.id]?.lastDate
                            ),
                            helperText: (
                              formik.touched.vehicleLastValues?.[vehicle.id]?.lastDate &&
                              (formik.errors.vehicleLastValues as any)?.[vehicle.id]?.lastDate
                            ) ? (formik.errors.vehicleLastValues as any)[vehicle.id].lastDate : null
                          }
                        }}
                      />
                    </Grid>
                  )}

                  {formik.values.usesInspection && (
                    <Grid item xs={12}>
                      <Box sx={{
                        p: 2,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1
                      }}>
                        <Typography variant="bodySmall" color="text.secondary" gutterBottom>
                          {t("checks.inspection_data", "Inspection details (automatically from Swedish Transport Agency)")}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t("vehicleregistry.latest_inspection", "Latest inspection")}:
                              </Typography>
                              <Typography variant="bodySmall" fontWeight="medium">
                                {vehicleInspectionData[vehicle.id]?.lastInspectionDate
                                  ? new Date(vehicleInspectionData[vehicle.id].lastInspectionDate).toLocaleDateString("sv-SE")
                                  : t("global.retrieved_automatically", "Retrieved automatically")}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t("global.next_inspection", "Next inspection")}:
                              </Typography>
                              <Typography variant="bodySmall" fontWeight="medium">
                                {vehicleInspectionData[vehicle.id]?.inspectionDeadline
                                  ? new Date(vehicleInspectionData[vehicle.id].inspectionDeadline).toLocaleDateString("sv-SE")
                                  : t("global.retrieved_automatically", "Retrieved automatically")}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {formik.values.usesDistance && (
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Box display="flex" gap={1} alignItems="center">
                          <StyledInputLabel>
                            <Typography variant="bodyLarge" color="text.primary">{t("global.latest_metersetting", "Latest meter reading")}</Typography>
                          </StyledInputLabel>

                          <Tooltip
                            sx={{ mb: 0.5 }}
                            title={
                              (() => {
                                const data = vehicleIoTOdometerData[vehicle.id]
                                if (!data) {
                                  return (
                                    <Typography variant="bodyXsmall">
                                      {t("global.loading_metersetting", "Loading metersetting")}...
                                    </Typography>
                                  )
                                }

                                let odometerText = ""
                                let colorStyle = "text.secondary"

                                if (data.currentOdometer) {
                                  const odometerValue = `${Math.round(data.currentOdometer).toLocaleString("sv-SE")} km`
                                  const sourceText = data.dataSource === OdometerDataSource.IoTDevice ? t("global.iot_device", "IoT-device") : t("global.manual_input", "Manual input")
                                  const recentText = data.dataSource === OdometerDataSource.IoTDevice && !data.isDataRecent ? ` (${t("global.older_data", "Older data")})` : ""
                                  odometerText = `${t("global.current", "Current")}: ${odometerValue} (${t("global.from", "from")} ${sourceText})${recentText}`

                                  if (data.dataSource === OdometerDataSource.IoTDevice) {
                                    colorStyle = data.isDataRecent ? "success.main" : "warning.main"
                                  } else if (data.dataSource === OdometerDataSource.ManualEntry) {
                                    colorStyle = (theme.palette as any).foreground.inverted
                                  }
                                } else if (data.hasIoTDevice && data.manualOdometer) {
                                  odometerText = `${t("checks.iot_no_current_data", "IoT device connected but no current data. Latest manual")}: ${Math.round(data.manualOdometer).toLocaleString("sv-SE")} km`
                                  colorStyle = "warning.main"
                                } else if (data.hasIoTDevice) {
                                  odometerText = t("checks.iot_device_missing2", "IoT device connected but no odometer reading available")
                                  colorStyle = "warning.main"
                                } else if (data.manualOdometer) {
                                  odometerText = `${t("global.latest_manual_value", "Latest manual input")}: ${Math.round(data.manualOdometer).toLocaleString("sv-SE")} km`
                                  colorStyle = (theme.palette as any).foreground.inverted
                                } else {
                                  odometerText = t("global.no_metersetting_available", "No meter reading available")
                                  colorStyle = "text.secondary"
                                }

                                return (
                                  <Typography variant="bodyXsmall" color={colorStyle}>
                                    {odometerText}
                                  </Typography>
                                )
                              })()
                            }
                          >
                            <FontAwesomeIcon
                              icon={faCircleInfo}
                              color={theme.palette["primary"].dark}
                            />
                          </Tooltip>
                        </Box>
                        <TextField
                          fullWidth
                          type="number"
                          value={(() => {
                            const formikValue = formik.values.vehicleLastValues[vehicle.id]?.lastDistance
                            if (formikValue !== undefined) return formikValue
                            const sessionValue = sessionServiceValues[vehicle.id]?.lastDistance
                            if (sessionValue !== undefined) {
                              formik.setFieldValue(`vehicleLastValues.${vehicle.id}.lastDistance`, sessionValue, false)
                              return sessionValue
                            }
                            const originalValue = originalServiceValues[vehicle.id]?.lastDistance
                            if (originalValue !== undefined) {
                              formik.setFieldValue(`vehicleLastValues.${vehicle.id}.lastDistance`, originalValue, false)
                              return originalValue
                            }
                            return ""
                          })()}
                          onChange={(e) => {
                            const value = e.target.value
                            if (value === "" || /^\d+$/.test(value)) {
                              const newValue = value !== "" ? Number(value) : undefined
                              formik.setFieldValue(
                                `vehicleLastValues.${vehicle.id}.lastDistance`,
                                newValue,
                                true
                              )
                            }
                          }}
                          onBlur={() => {
                            formik.setFieldTouched(`vehicleLastValues.${vehicle.id}.lastDistance`, true)
                            const currentValue = formik.values.vehicleLastValues[vehicle.id]?.lastDistance
                            setSessionServiceValues((prev: any) => ({
                              ...prev,
                              [vehicle.id]: {
                                ...prev[vehicle.id],
                                lastDistance: currentValue
                              }
                            }))
                          }}
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 0
                          }}
                          error={Boolean(
                            formik.touched.vehicleLastValues?.[vehicle.id]?.lastDistance &&
                            (formik.errors.vehicleLastValues as any)?.[vehicle.id]?.lastDistance
                          )}
                          helperText={
                            formik.touched.vehicleLastValues?.[vehicle.id]?.lastDistance &&
                              (formik.errors.vehicleLastValues as any)?.[vehicle.id]?.lastDistance
                              ? (formik.errors.vehicleLastValues as any)[vehicle.id].lastDistance
                              : null
                          }
                        />

                      </Box>
                    </Grid>
                  )}
                </Grid>
              </StyledVehicleBox>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}
